/**
 * Pure helpers shared by the Worker and the web app. No runtime dependencies,
 * no platform APIs beyond what both runtimes provide (fetch-era globals only).
 */

import type { PlatformId, PostStatus, PlatformPostStatus } from './types';

/* ------------------------------------------------------------------ */
/* Text                                                                */
/* ------------------------------------------------------------------ */

export function slugify(input: string, maxLength = 60): string {
  const slug = input
    .normalize('NFKD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .replace(/['’]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
  return slug.slice(0, maxLength).replace(/-+$/g, '');
}

/** Truncate on a word boundary and append an ellipsis. */
export function truncate(input: string, maxLength: number, suffix = '…'): string {
  const text = input.trim();
  if (text.length <= maxLength) return text;
  const cut = text.slice(0, Math.max(0, maxLength - suffix.length));
  const lastSpace = cut.lastIndexOf(' ');
  return (lastSpace > maxLength * 0.6 ? cut.slice(0, lastSpace) : cut).trimEnd() + suffix;
}

/** Single-line preview of a post body (strips markup and collapses whitespace). */
export function excerpt(input: string, maxLength = 140): string {
  return truncate(stripHtml(input).replace(/\s+/g, ' ').trim(), maxLength);
}

export function stripHtml(input: string): string {
  return input
    .replace(/<style[\s\S]*?<\/style>/gi, '')
    .replace(/<script[\s\S]*?<\/script>/gi, '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, ' ')
    .trim();
}

export function escapeHtml(input: string): string {
  return input
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

export function extractUrls(text: string): string[] {
  return text.match(/https?:\/\/[^\s<>"')]+/g) ?? [];
}

export function firstUrl(text: string): string | null {
  return extractUrls(text)[0] ?? null;
}

export function wordCount(text: string): number {
  const trimmed = stripHtml(text).trim();
  return trimmed ? trimmed.split(/\s+/).length : 0;
}

/** Rough reading time in minutes (225 wpm), minimum 1. */
export function readingMinutes(text: string): number {
  return Math.max(1, Math.round(wordCount(text) / 225));
}

export function normalizeHashtag(tag: string): string {
  const clean = tag.trim().replace(/^#+/, '').replace(/[^\p{L}\p{N}_]/gu, '');
  return clean ? `#${clean}` : '';
}

export function normalizeHashtags(tags: readonly string[]): string[] {
  const seen = new Set<string>();
  const out: string[] = [];
  for (const tag of tags) {
    const normalized = normalizeHashtag(tag);
    if (!normalized) continue;
    const key = normalized.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(normalized);
  }
  return out;
}

/** Append hashtags to a body if they are not already present in it. */
export function appendHashtags(content: string, tags: readonly string[]): string {
  const normalized = normalizeHashtags(tags);
  const missing = normalized.filter(
    (tag) => !new RegExp(`(^|\\s)${escapeRegExp(tag)}(\\s|$)`, 'iu').test(content),
  );
  if (!missing.length) return content;
  return `${content.trimEnd()}\n\n${missing.join(' ')}`;
}

export function escapeRegExp(input: string): string {
  return input.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

export function initials(nameOrEmail: string): string {
  const source = nameOrEmail.includes('@') ? nameOrEmail.split('@')[0] ?? '' : nameOrEmail;
  const parts = source.split(/[\s._-]+/).filter(Boolean);
  if (!parts.length) return '?';
  if (parts.length === 1) return (parts[0] ?? '?').slice(0, 2).toUpperCase();
  return `${parts[0]?.[0] ?? ''}${parts[1]?.[0] ?? ''}`.toUpperCase();
}

export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email.trim());
}

export function normalizeEmail(email: string): string {
  return email.trim().toLowerCase();
}

/** Normalize a user-typed site URL to an origin without a trailing slash. */
export function normalizeSiteUrl(input: string): string | null {
  const raw = input.trim();
  if (!raw) return null;
  const withScheme = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
  try {
    const url = new URL(withScheme);
    return `${url.protocol}//${url.host}${url.pathname.replace(/\/+$/, '')}`;
  } catch {
    return null;
  }
}

/* ------------------------------------------------------------------ */
/* Time                                                                */
/* ------------------------------------------------------------------ */

export const MINUTE_MS = 60_000;
export const HOUR_MS = 3_600_000;
export const DAY_MS = 86_400_000;

export function nowMs(): number {
  return Date.now();
}

/** Accepts ISO strings, epoch ms numbers or Dates; returns epoch ms or null. */
export function toEpochMs(value: string | number | Date | null | undefined): number | null {
  if (value === null || value === undefined || value === '') return null;
  if (typeof value === 'number') return Number.isFinite(value) ? value : null;
  if (value instanceof Date) {
    const ms = value.getTime();
    return Number.isNaN(ms) ? null : ms;
  }
  const parsed = Date.parse(value);
  return Number.isNaN(parsed) ? null : parsed;
}

export function toIso(value: number | Date | null | undefined): string | null {
  const ms = toEpochMs(value ?? null);
  return ms === null ? null : new Date(ms).toISOString();
}

/** Start of the UTC day containing `ms`. */
export function startOfUtcDay(ms: number): number {
  const date = new Date(ms);
  date.setUTCHours(0, 0, 0, 0);
  return date.getTime();
}

export function addMinutes(ms: number, minutes: number): number {
  return ms + minutes * MINUTE_MS;
}

export function addDays(ms: number, days: number): number {
  return ms + days * DAY_MS;
}

export function isPast(ms: number | null | undefined, reference = Date.now()): boolean {
  return typeof ms === 'number' && ms <= reference;
}

/**
 * Retry backoff for failed publishes: attempt 1 → +2m, 2 → +10m, 3 → +30m.
 * Returns null once the attempts are exhausted.
 */
export const RETRY_BACKOFF_MINUTES = [2, 10, 30] as const;
export const MAX_PUBLISH_RETRIES = RETRY_BACKOFF_MINUTES.length;

export function nextRetryAt(retryCount: number, from = Date.now()): number | null {
  const minutes = RETRY_BACKOFF_MINUTES[retryCount];
  return minutes === undefined ? null : addMinutes(from, minutes);
}

/** IANA timezone → offset string like "GMT-5" for a given instant. */
export function timezoneLabel(timezone: string, at = Date.now()): string {
  try {
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone: timezone,
      timeZoneName: 'shortOffset',
    });
    const part = formatter.formatToParts(new Date(at)).find((p) => p.type === 'timeZoneName');
    return part?.value ?? timezone;
  } catch {
    return timezone;
  }
}

export function isValidTimezone(timezone: string): boolean {
  try {
    new Intl.DateTimeFormat('en-US', { timeZone: timezone });
    return true;
  } catch {
    return false;
  }
}

/* ------------------------------------------------------------------ */
/* Collections & objects                                               */
/* ------------------------------------------------------------------ */

export function unique<T>(items: readonly T[]): T[] {
  return [...new Set(items)];
}

export function groupBy<T, K extends string>(
  items: readonly T[],
  keyFn: (item: T) => K,
): Record<K, T[]> {
  const out = {} as Record<K, T[]>;
  for (const item of items) {
    const key = keyFn(item);
    (out[key] ??= []).push(item);
  }
  return out;
}

export function chunk<T>(items: readonly T[], size: number): T[][] {
  if (size <= 0) return [items.slice()];
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

export function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

/** Remove `undefined` values so PATCH bodies only carry real changes. */
export function compact<T extends Record<string, unknown>>(input: T): Partial<T> {
  const out: Partial<T> = {};
  for (const [key, value] of Object.entries(input)) {
    if (value !== undefined) out[key as keyof T] = value as T[keyof T];
  }
  return out;
}

export function safeJsonParse<T>(input: string | null | undefined, fallback: T): T {
  if (!input) return fallback;
  try {
    return JSON.parse(input) as T;
  } catch {
    return fallback;
  }
}

/** Build a query string from a params object, skipping empty values. */
export function buildQuery(
  params: Record<string, string | number | boolean | null | undefined>,
): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value === null || value === undefined || value === '') continue;
    search.set(key, String(value));
  }
  const query = search.toString();
  return query ? `?${query}` : '';
}

/* ------------------------------------------------------------------ */
/* Domain helpers                                                      */
/* ------------------------------------------------------------------ */

/**
 * Derive the parent post status from its platform posts.
 * published > all done; partial > some published & some failed/skipped.
 */
export function derivePostStatus(
  children: ReadonlyArray<{ status: PlatformPostStatus }>,
  current: PostStatus = 'draft',
): PostStatus {
  if (current === 'archived') return 'archived';
  if (!children.length) return current === 'published' ? 'draft' : current;

  const statuses = children.map((child) => child.status);
  const active = statuses.filter((status) => status !== 'skipped');
  if (!active.length) return 'draft';

  if (active.some((status) => status === 'publishing')) return 'publishing';

  const published = active.filter((status) => status === 'published').length;
  const failed = active.filter((status) => status === 'failed').length;

  if (published === active.length) return 'published';
  if (failed === active.length) return 'failed';
  if (published > 0) return 'partial';
  if (active.some((status) => status === 'scheduled')) return 'scheduled';
  return 'draft';
}

const PLATFORM_POST_STATUS_LABELS: Record<PlatformPostStatus, string> = {
  draft: 'Draft',
  approved: 'Approved',
  scheduled: 'Scheduled',
  publishing: 'Publishing',
  published: 'Published',
  failed: 'Failed',
  skipped: 'Skipped',
};

const POST_STATUS_LABELS: Record<PostStatus, string> = {
  draft: 'Draft',
  scheduled: 'Scheduled',
  publishing: 'Publishing',
  published: 'Published',
  partial: 'Partly published',
  failed: 'Failed',
  archived: 'Archived',
};

export function postStatusLabel(status: PostStatus): string {
  return POST_STATUS_LABELS[status] ?? status;
}

export function platformPostStatusLabel(status: PlatformPostStatus): string {
  return PLATFORM_POST_STATUS_LABELS[status] ?? status;
}

/** Stable, human-friendly R2 object key for an upload. */
export function mediaKey(workspaceId: string, assetId: string, filename: string): string {
  const safeName = filename.replace(/[^a-zA-Z0-9._-]+/g, '-').slice(-80) || 'file';
  return `${workspaceId}/${assetId}/${safeName}`;
}

export function renditionKey(
  workspaceId: string,
  assetId: string,
  platform: PlatformId,
  label: string,
): string {
  return `${workspaceId}/${assetId}/renditions/${platform}-${slugify(label) || 'rendition'}.jpg`;
}

/** App-relative URL for a stored R2 object. */
export function mediaFilePath(r2Key: string): string {
  return `/api/media/file/${r2Key.split('/').map(encodeURIComponent).join('/')}`;
}

export function formatBytes(bytes: number): string {
  if (!Number.isFinite(bytes) || bytes <= 0) return '0 B';
  const units = ['B', 'KB', 'MB', 'GB', 'TB'];
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1);
  const value = bytes / 1024 ** exponent;
  return `${value >= 10 || exponent === 0 ? Math.round(value) : value.toFixed(1)} ${units[exponent]}`;
}

/** Deterministic 32-bit hash — used by the mock AI provider for stable output. */
export function hashString(input: string): number {
  let hash = 2166136261;
  for (let i = 0; i < input.length; i++) {
    hash ^= input.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return hash >>> 0;
}

/** Pick a deterministic element from a list based on a seed string. */
export function pickDeterministic<T>(items: readonly T[], seed: string): T {
  if (!items.length) throw new Error('pickDeterministic: empty list');
  return items[hashString(seed) % items.length] as T;
}
